import { inject, observer } from 'mobx-react'
import useMediaQuery from '@mui/material/useMediaQuery'

import { withRouterProps } from '../../../utils'
import { numberToDollars, roundPercent, formatPercentLabel } from '../../../utils/utils'
import { RetirementIncomeDonut, StatusPill } from '../../../components'
import { chartWidth } from '../../../components/RetirementIncomeDonut'
import { DEFAULT_RETIREMENT_INCOME_GOAL_PCT } from '../../../models/Person'
import { TABLET_VIEWPORT } from '../../../guided-toolbox/utils/breakpoints'
import RetiredTodayButton from './RetiredTodayButton'
import {
  SectionHeading,
  PlanHeadingRow,
  PlanBody,
  PlanChart,
  PlanCtaRow,
  GetAdviceButton,
  GoalLine,
} from './styles'

const HOME_DONUT_RADIUS = 150
const HOME_DONUT_LAYOUT = {
  radius: HOME_DONUT_RADIUS,
  ringThickness: 20,
  amountOffsetY: -40,
  amountFontSize: 40,
  monthlyOffsetY: -12,
  monthlyFontSize: 14,
  subLabelOffsetY: 24,
  subLabelFontSize: 14,
}

// Prevent the single-button CTA row from shrinking the donut below its natural width.
const HOME_DONUT_WIDTH = chartWidth(HOME_DONUT_RADIUS)

const YourPlan = ({ navigate, mode, store: { baseCase, person } }) => {
  const isMobileOrTablet = useMediaQuery(TABLET_VIEWPORT)

  if (!baseCase) {
    return null
  }

  const goalPercent = person?.retirementIncomeGoalPct ?? DEFAULT_RETIREMENT_INCOME_GOAL_PCT
  const replacementPercent = baseCase.retirementIncomeGoal

  return (
    <>
      <PlanHeadingRow>
        <SectionHeading>{isMobileOrTablet ? 'Your retirement income' : 'Your plan'}</SectionHeading>
        <StatusPill isOnTrack={baseCase.isOnTrack} />
      </PlanHeadingRow>

      <PlanBody $minWidth={HOME_DONUT_WIDTH}>
        <PlanChart>
          <RetirementIncomeDonut
            animate
            amount={baseCase.projectedMonthlyRetirementInc}
            replacementPercent={replacementPercent}
            goalPercent={goalPercent}
            layout={HOME_DONUT_LAYOUT}
            subLabel={`${roundPercent(replacementPercent)}% of current income`}
            goalCallout={
              isMobileOrTablet
                ? undefined
                : { label: 'Goal:', amount: baseCase.targetedAftTaxIncome, percent: goalPercent }
            }
          />

          {isMobileOrTablet && (
            <GoalLine>
              Goal: ${numberToDollars(baseCase.targetedAftTaxIncome, true)} (
              {formatPercentLabel(goalPercent)}%)
            </GoalLine>
          )}
        </PlanChart>

        <PlanCtaRow>
          <GetAdviceButton onClick={() => navigate('/advice', { state: { from: '/home' } })}>
            Get advice
          </GetAdviceButton>
          {mode === 'retWithinThreeYears' && <RetiredTodayButton />}
        </PlanCtaRow>
      </PlanBody>
    </>
  )
}

export default withRouterProps(inject('store')(observer(YourPlan)))
